import { useEffect, useState } from "react";
import { ArrowUpRight, Activity, Fuel } from "lucide-react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { FadeIn } from "@/components/Reveal";
import SEO from "@/components/SEO";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function MarketWire() {
  const [market, setMarket] = useState(null);
  const [rates, setRates] = useState([]);

  useEffect(() => {
    api.get("/market/news").then((r) => setMarket(r.data)).catch(() => setMarket({ items: [], crude: null }));
    api.get("/market/rates").then((r) => setRates(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="market-wire">
      {rates.length > 0 && (
        <div className="mb-14">
          <FadeIn>
            <p className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
              <Activity className="h-4 w-4 text-brand" /> Indicative Rate Board
            </p>
          </FadeIn>
          <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="rate-board">
            {rates.map((r, i) => (
              <FadeIn key={r.id} delay={i * 0.05} className="bg-[#0A0A0A] p-7">
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">{r.grade}</p>
                <p className="mt-4 font-mono text-2xl font-medium text-brand" data-testid={`rate-price-${i}`}>
                  {r.price}
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-600">{r.unit}</p>
                {r.note && <p className="mt-4 border-t border-white/5 pt-3 text-xs text-neutral-500">{r.note}</p>}
                <p className="mt-3 font-mono text-[10px] text-neutral-600">Updated {timeAgo(r.updated_at)}</p>
              </FadeIn>
            ))}
          </div>
          {market?.crude && (
            <FadeIn className="mt-4 flex flex-wrap items-center gap-4 border border-white/10 bg-[#0C0C0C] px-6 py-4" data-testid="crude-ticker">
              <Fuel className="h-4 w-4 text-brand" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-400">{market.crude.name} ({market.crude.symbol})</span>
              <span className="font-mono text-lg text-white">${market.crude.price}</span>
              <span className="font-mono text-[10px] text-neutral-600">as of {market.crude.as_of}</span>
              <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">Bitumen tracks crude — ask us for today&apos;s rate</span>
            </FadeIn>
          )}
        </div>
      )}

      <FadeIn>
        <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          Live Bitumen News Wire {market?.fetched_at ? `· refreshed ${timeAgo(market.fetched_at)}` : ""}
        </p>
      </FadeIn>
      {!market ? (
        <div className="space-y-4">{[...Array(5)].map((_, i) => <div key={i} className="h-20 animate-pulse border border-white/10 bg-[#101010]" />)}</div>
      ) : market.items.length === 0 ? (
        <p className="text-sm text-neutral-500" data-testid="market-empty">Live wire is warming up — check back shortly.</p>
      ) : (
        <div data-testid="market-list">
          {market.items.map((n, i) => (
            <FadeIn key={n.id} delay={Math.min(i * 0.03, 0.4)}>
              <a
                href={n.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`market-item-${i}`}
                className="group grid gap-2 border-t border-white/10 py-6 transition-colors last:border-b hover:bg-white/[0.02] md:grid-cols-12 md:items-baseline"
              >
                <p className="font-mono text-[11px] tracking-[0.15em] text-brand md:col-span-3">
                  {n.source} · {timeAgo(n.published)}
                </p>
                <h3 className="text-base font-medium text-white transition-colors group-hover:text-brand md:col-span-8 md:text-lg">
                  {n.title}
                </h3>
                <ArrowUpRight className="hidden h-4 w-4 text-neutral-600 transition-all group-hover:translate-x-1 group-hover:text-brand md:col-span-1 md:block" />
              </a>
            </FadeIn>
          ))}
        </div>
      )}
      <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-600">
        Headlines aggregated from public news sources via Google News — stories open on the publisher&apos;s site.
      </p>
    </div>
  );
}

export default function News() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const [view, setView] = useState("company");

  useEffect(() => {
    api.get("/news").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  if (!items) return <div className="min-h-screen bg-[#0A0A0A]" data-testid="news-loading" />;

  return (
    <main data-testid="news-page">
      <SEO
        title="News, Market Wire & Bitumen Rates — Catalyst Petrochem"
        description="Company updates plus a live wire of bitumen market news, Brent crude prices and indicative rates — refreshed through the day."
        path="/news"
      />
      <PageHeader
        index="07"
        tag="News & Updates"
        title="Dispatches from the field."
        description="Company milestones plus a live wire of bitumen market news and indicative rates — refreshed through the day."
      />
      <section className="px-6 py-16 md:px-12 md:py-20 lg:px-20">
        <div className="mb-12 flex gap-3" data-testid="news-view-toggle">
          {[
            ["company", "Company Updates"],
            ["market", "Market Wire · Live"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setView(key)}
              data-testid={`news-view-${key}`}
              className={`border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                view === key
                  ? "border-brand bg-brand text-white"
                  : "border-white/15 text-neutral-400 hover:border-brand hover:text-brand"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {view === "market" ? (
          <MarketWire />
        ) : items.length === 0 ? (
          <p className="text-sm text-neutral-500" data-testid="news-empty">No updates published yet.</p>
        ) : (
          <div data-testid="news-list">
            {items.map((n, i) => {
              const isOpen = open === n.id;
              return (
                <FadeIn key={n.id} delay={i * 0.05}>
                  <article className="border-t border-white/10 py-10 last:border-b" data-testid={`news-item-${n.id}`}>
                    <button
                      onClick={() => setOpen(isOpen ? null : n.id)}
                      data-testid={`news-toggle-${n.id}`}
                      className="grid w-full gap-4 text-left md:grid-cols-12 md:items-baseline"
                    >
                      <p className="font-mono text-xs tracking-[0.2em] text-brand md:col-span-2">
                        {n.date} · {n.tag}
                      </p>
                      <div className="md:col-span-10">
                        <h2 className="font-display text-2xl font-medium uppercase tracking-normal text-white transition-colors duration-300 hover:text-brand md:text-3xl">
                          {n.title}
                        </h2>
                        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-neutral-500">{n.summary}</p>
                        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-600">
                          {isOpen ? "— Close" : "+ Read more"}
                        </p>
                      </div>
                    </button>
                    {isOpen && n.body && (
                      <div className="mt-6 grid gap-4 md:grid-cols-12" data-testid={`news-body-${n.id}`}>
                        <div className="md:col-span-2" />
                        <p className="text-sm leading-relaxed text-neutral-300 md:col-span-8 md:text-base">{n.body}</p>
                      </div>
                    )}
                  </article>
                </FadeIn>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
